import React, { useState } from 'react';
import { Button, TextField, Typography, Box, Paper, Alert, CircularProgress, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import InsightsIcon from '@mui/icons-material/Insights';
import BrandMark from './BrandMark';
import { login, resendVerification } from '../services/api';

// Sin refresh token: la sesión dura 48hs y al vencer el interceptor de axios
// (api.js) fuerza logout y deja esta marca para explicar por qué, en vez de
// mostrar el login en blanco como si el usuario nunca hubiese estado adentro.
const hadSessionExpired = () => {
  const expired = sessionStorage.getItem('session_expired');
  if (expired) sessionStorage.removeItem('session_expired');
  return !!expired;
};

// Cambiar la contraseña invalida el JWT actual en el backend (ver
// auth.get_current_user) — el próximo request de la sesión activa fallaría
// con 401 y caería en el mensaje genérico de "expiró por inactividad", que
// es engañoso acá (el usuario mismo lo causó, con éxito). ChangePasswordDialog
// hace logout explícito con esta marca propia en vez de dejar que ese 401
// genérico dispare solo.
const hadPasswordChanged = () => {
  const changed = sessionStorage.getItem('password_changed');
  if (changed) sessionStorage.removeItem('password_changed');
  return !!changed;
};

const SIDE_HIGHLIGHTS = [
  { icon: EventAvailableIcon, text: 'Agenda y turnos en un solo lugar' },
  { icon: FolderSharedIcon, text: 'Historia clínica e historial de cada paciente' },
  { icon: InsightsIcon, text: 'Métricas del consultorio al instante' },
];

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [sessionExpired] = useState(hadSessionExpired);
  const [passwordChanged] = useState(hadPasswordChanged);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendState('idle');
    if (!username.trim()) { setError('Ingresá tu usuario'); return; }
    if (!password) { setError('Ingresá tu contraseña'); return; }
    setLoading(true);
    try {
      const response = await login({ username, password });
      const newToken = response.data.access_token;
      localStorage.setItem('token', newToken);
      // No hardcodear una ruta acá: dejar que App.js decida vía
      // getDefaultRoute() según el rol real (admin → /metrics, recepcionista
      // → /dashboard-recepcionista, etc.) una vez que cargue el usuario.
      // Antes esto mandaba a todos a /agenda sin importar el rol.
      window.location.href = '/';
    } catch (err) {
      if (err.response?.data?.detail === 'email_not_verified') {
        setNeedsVerification(true);
        setError('Todavía no confirmaste tu email. Revisá tu casilla (y spam) o reenviá el link.');
      } else if (err.response?.data?.detail === 'user_deactivated') {
        setError('Tu usuario fue dado de baja. Contactá al administrador de tu clínica.');
      } else {
        setError(err.userMessage || 'Credenciales inválidas o error de conexión');
      }
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!resendEmail.trim()) return;
    setResendState('sending');
    try {
      await resendVerification(resendEmail.trim());
    } finally {
      setResendState('sent');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: 'background.default', p: 2,
    }}>
      <Paper elevation={0} sx={{
        display: 'flex', width: '100%', maxWidth: 880, borderRadius: 3, overflow: 'hidden',
        border: '1px solid', borderColor: 'divider',
      }}>
        {/* Panel de marca — oculto en mobile, la identidad no compite con el form en pantallas chicas */}
        <Box sx={{
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column', justifyContent: 'center',
          width: 340, flexShrink: 0, p: 5,
          background: (theme) => `linear-gradient(160deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 4 }}>
            <BrandMark size={28} variant="bare" />
            <Typography variant="h5" fontWeight={700}>VitaNotas</Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.92, mb: 4, lineHeight: 1.6 }}>
            Gestión simple para consultorios y clínicas chicas.
          </Typography>
          <Stack spacing={2}>
            {SIDE_HIGHLIGHTS.map(({ icon: Icon, text }) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Icon sx={{ fontSize: 20, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.92 }}>{text}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Formulario */}
        <Box sx={{ flex: 1, p: { xs: 3.5, sm: 5 }, minWidth: 0 }}>
          {/* Marca visible solo en mobile, donde el panel lateral está oculto */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mb: 3 }}>
            <BrandMark size={26} />
            <Typography variant="h6" fontWeight={700} color="text.primary">VitaNotas</Typography>
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            Iniciar sesión
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ingresá con tu usuario y contraseña de la clínica.
          </Typography>

          {passwordChanged ? (
            <Alert severity="success" sx={{ mb: 2.5 }}>
              Tu contraseña se actualizó correctamente. Iniciá sesión de nuevo con la nueva.
            </Alert>
          ) : sessionExpired && (
            <Alert severity="info" sx={{ mb: 2.5 }}>
              Tu sesión expiró por inactividad. Volvé a ingresar para continuar.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              error={!username.trim() && !!error}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!password && !!error}
            />
            {error && <Alert severity={needsVerification ? 'warning' : 'error'} sx={{ mt: 1.5 }}>{error}</Alert>}

            {needsVerification && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                {resendState === 'sent' ? (
                  <Typography variant="body2" color="success.main">
                    Si ese email corresponde a una cuenta pendiente, te reenviamos el link. Revisá tu casilla.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Reenviar link de confirmación a:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small" fullWidth type="email" placeholder="tu@email.com"
                        value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                      />
                      <Button
                        variant="outlined" size="small" onClick={handleResend}
                        disabled={!resendEmail.trim() || resendState === 'sending'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {resendState === 'sending' ? <CircularProgress size={16} /> : 'Reenviar'}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3, mb: 2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Ingresar'}
            </Button>

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: '#1565c0', textDecoration: 'none' }}>
                ¿Olvidaste tu contraseña?
              </Link>
              <Link to="/register" style={{ fontSize: 13, color: '#1565c0', textDecoration: 'none' }}>
                Registrar mi clínica →
              </Link>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
